import { useEffect, useMemo, useState } from 'react'
import { Loader2, TrendingUp, TrendingDown, Minus, Wallet, AlertTriangle } from 'lucide-react'
import { api } from '../lib/api.js'
import { CARD, PeriodPills } from '../components/ui.jsx'
import { thb } from '../lib/billing.js'
import { sumRange } from '../lib/revenue.js'
import { smoothPath } from '../lib/chart.js'

/**
 * Branch-9 treatment-income dashboard for the Operations page. Controlled by
 * the shared Operations-page period filter (see src/lib/periods.js) so it stays
 * in lock-step with the activity hero + op-days trend. Income is summed PER
 * OPERATION (each op's own chart/revenue query — the same the Operations-list
 * income column uses) because the Xano chart/revenue report is range-sensitive;
 * a single wide call undercounts. `ops` (the window's operations) is fetched
 * once by the parent and passed in.
 */

export function IncomeDashboard({ ops, win, period, onPeriodChange }) {
  const [byDay, setByDay] = useState(null)
  const [err, setErr]     = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (ops === 'error') { setErr('โหลดข้อมูลไม่สำเร็จ'); setByDay(null); setLoading(false); return }
    if (!ops) { setByDay(null); setLoading(true); return }
    let cancelled = false
    setLoading(true); setErr(null)
    // Per-operation revenue, bucketed on each op's start date for the daily
    // chart. (Assumes operations don't share dates — true for a single mobile
    // unit — otherwise the branch-wide revenue would double-count.)
    ;(async () => {
      const map = new Map()
      await mapLimit(ops, 6, async (op) => {
        if (cancelled || !op?.start_date) return
        const d = await api.revenue.range(op.start_date, op.end_date || op.start_date).catch(() => null)
        if (!d) return
        let total = 0, clinic = 0
        for (const day of d.days || []) { total += Number(day.total) || 0; clinic += Number(day.clinic) || 0 }
        const cur = map.get(op.start_date) || { total: 0, clinic: 0 }
        cur.total += total; cur.clinic += clinic
        map.set(op.start_date, cur)
      })
      if (!cancelled) { setByDay(map); setLoading(false) }
    })()
    return () => { cancelled = true }
  }, [ops])

  const view = useMemo(() => {
    if (!byDay) return null
    const bars = [...byDay.entries()]
      .filter(([d]) => d >= win.curStart && d <= win.curEnd)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, v]) => ({ date, total: v.total, clinic: v.clinic }))
    const cur = sumRange(byDay, win.curStart, win.curEnd)
    const prev = win.prevStart ? sumRange(byDay, win.prevStart, win.prevEnd) : null
    const deltaPct = (prev && prev.total > 0) ? ((cur.total - prev.total) / prev.total) * 100 : null
    return { bars, cur, prev, deltaPct }
  }, [byDay, win])

  return (
    <div className={`${CARD} p-4 md:p-5 mb-4`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
            <Wallet size={17} />
          </div>
          <div>
            <h2 className="text-[15px] font-bold text-zinc-900 leading-tight">รายได้จากการรักษา</h2>
            <p className="text-[11.5px] text-zinc-500">Tooth Taxi · branch 9 · รายวัน</p>
          </div>
        </div>
        <div className="overflow-x-auto w-full sm:w-auto"><PeriodPills value={period} onChange={onPeriodChange} /></div>
      </div>

      {err ? (
        <div className="px-3 py-6 text-center text-rose-600 text-[12.5px] inline-flex items-center gap-2">
          <AlertTriangle size={14} /> {err}
        </div>
      ) : !view && loading ? (
        <div className="px-3 py-10 text-center text-zinc-400 text-[12.5px]">
          <Loader2 size={16} className="inline animate-spin mr-1.5" /> กำลังโหลดรายได้…
        </div>
      ) : view ? (
        <div className={loading ? 'opacity-60 transition-opacity' : ''}>
          <Summary cur={view.cur} prev={view.prev} deltaPct={view.deltaPct} />
          <Chart bars={view.bars} />
        </div>
      ) : null}
    </div>
  )
}

function Summary({ cur, prev, deltaPct }) {
  const up = deltaPct != null && deltaPct >= 0
  const TrendIcon = deltaPct == null ? Minus : up ? TrendingUp : TrendingDown
  const trendCls = deltaPct == null
    ? 'text-zinc-400 bg-zinc-50 ring-zinc-200'
    : up ? 'text-emerald-700 bg-emerald-50 ring-emerald-200' : 'text-rose-700 bg-rose-50 ring-rose-200'
  return (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-2 mb-3">
      <div>
        <div className="text-[26px] md:text-[30px] font-bold text-zinc-900 tabular-nums leading-none">{thb(cur.total)}</div>
        <div className="text-[11px] text-zinc-500 mt-1">รายได้รวม (Total)</div>
      </div>
      <div className="pb-0.5">
        <div className="text-[18px] font-bold text-emerald-700 tabular-nums leading-none">{thb(cur.clinic)}</div>
        <div className="text-[11px] text-zinc-500 mt-1">ส่วนของคลินิก (Clinic)</div>
      </div>
      {prev != null && (
        <div className="flex items-center gap-2 pb-0.5">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md ring-1 text-[12px] font-semibold tabular-nums ${trendCls}`}>
            <TrendIcon size={13} />
            {deltaPct == null ? '—' : `${up ? '+' : ''}${deltaPct.toFixed(1)}%`}
          </span>
          <span className="text-[11.5px] text-zinc-500">vs ช่วงก่อนหน้า ({thb(prev.total, { compact: true })})</span>
        </div>
      )}
    </div>
  )
}

// Segmented toggle picking which series the chart plots.
const METRICS = [
  { key: 'total',  label: 'รวม Total', dot: 'bg-emerald-500', line: 'rgb(16 185 129)', area: 'rgb(167 243 208 / 0.45)' },
  { key: 'clinic', label: 'คลินิก',    dot: 'bg-emerald-600', line: 'rgb(5 150 105)', area: 'rgb(110 231 183 / 0.32)' },
]

function MetricToggle({ value, onChange }) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg bg-zinc-100/80 p-0.5 ring-1 ring-zinc-200/60">
      {METRICS.map((m) => {
        const on = m.key === value
        return (
          <button
            key={m.key}
            type="button"
            onClick={() => onChange(m.key)}
            className={
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-medium transition-colors ' +
              (on ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-900')
            }
          >
            <span className={`w-2 h-2 rounded-full ${m.dot}`} />
            {m.label}
          </button>
        )
      })}
    </div>
  )
}

function Chart({ bars }) {
  const [metric, setMetric] = useState('total')   // 'total' | 'clinic'
  const m = METRICS.find((x) => x.key === metric)
  const max = useMemo(() => Math.max(1, ...bars.map((b) => b[metric])), [bars, metric])

  if (!bars.length) {
    return <div className="py-10 text-center text-zinc-400 text-[12.5px]">ไม่มีข้อมูลรายได้ในช่วงนี้</div>
  }

  const labelEvery = Math.ceil(bars.length / 12)

  // Single selected series plotted in a 0..100 viewBox; the SVG is stretched to
  // fill the container with preserveAspectRatio="none", so x/y are pure
  // percentages. `vectorEffect="non-scaling-stroke"` keeps the line weight
  // constant. A single data point renders centered (x=50).
  const n = bars.length
  const xAt = (i) => (n === 1 ? 50 : (i / (n - 1)) * 100)
  const yAt = (v) => 100 - (Math.max(0, v) / max) * 100
  const pts = bars.map((b, i) => ({ x: xAt(i), y: yAt(b[metric]) }))
  const linePath = smoothPath(pts)
  const areaPath = `M 0,100 ${linePath.replace(/^M /, 'L ')} L 100,100 Z`

  return (
    <div className="w-full">
      <div className="flex items-center justify-end mb-2">
        <MetricToggle value={metric} onChange={setMetric} />
      </div>
      <div className="relative h-44">
        <svg className="absolute inset-0 w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d={areaPath} fill={m.area} stroke="none" />
          <path
            d={linePath} fill="none" stroke={m.line} strokeWidth="2"
            vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round"
          />
        </svg>
        {/* Invisible hover columns — one per day — driving the tooltip + a hover guide line. */}
        <div className="absolute inset-0 flex">
          {bars.map((b) => (
            <div key={b.date} className="flex-1 min-w-0 h-full group relative">
              <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-transparent group-hover:bg-zinc-200/80" />
              <div className="pointer-events-none absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-10 hidden group-hover:block whitespace-nowrap rounded-md bg-zinc-900 text-white text-[11px] px-2 py-1 shadow-lg">
                <div className={metric === 'total' ? 'font-semibold tabular-nums' : 'tabular-nums text-zinc-300'}>รวม {thb(b.total)}</div>
                <div className={metric === 'clinic' ? 'font-semibold tabular-nums text-emerald-300' : 'tabular-nums text-emerald-300/70'}>คลินิก {thb(b.clinic)}</div>
                <div className="text-zinc-300 text-[10px]">{thDate(b.date)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-[3px] mt-1.5">
        {bars.map((b, i) => (
          <div key={b.date} className="flex-1 min-w-0 text-center text-[9px] text-zinc-400 tabular-nums truncate">
            {i % labelEvery === 0 ? shortDate(b.date) : ''}
          </div>
        ))}
      </div>
    </div>
  )
}

// Run async `fn` over `items` with at most `limit` in flight at once — keeps
// the per-operation revenue fan-out from opening dozens of sockets at once.
async function mapLimit(items, limit, fn) {
  const queue = items.slice()
  const workers = Array.from({ length: Math.min(limit, queue.length) }, async () => {
    while (queue.length) await fn(queue.shift())
  })
  await Promise.all(workers)
}

const TH_MONTHS =['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
function shortDate(d) { const [, m, dd] = d.split('-'); return `${Number(dd)} ${TH_MONTHS[Number(m) - 1]}` }
function thDate(d) { const [y, m, dd] = d.split('-'); return `${Number(dd)} ${TH_MONTHS[Number(m) - 1]} ${Number(y) + 543}` }
